import { Directive, ElementRef, HostListener, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Directive({
  selector: "input[Number]"
})
export class NumberDirective {
  constructor(private _el: ElementRef) { }

  @HostListener("input", ["$event"]) onInputChange(event) {
    const initalValue = this._el.nativeElement.value;
    this._el.nativeElement.value = initalValue.replace(/[^0-9]*/g, "");
    if (initalValue !== this._el.nativeElement.value) {
      event.stopPropagation();
    }
  }
}

@Directive({
  selector: "input[PositiveNumber]"
})
export class PositiveNumberDirective {

  private regex: RegExp = new RegExp(/^[1-9][0-9]*$/g);
  private specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Del', 'Delete'];
  constructor(private el: ElementRef) {
  }
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Allow Backspace, tab, end, and home keys
    if (this.specialKeys.indexOf(event.key) !== -1) {
      return;
    }
    const current: string = this.el.nativeElement.value;
    const position = this.el.nativeElement.selectionStart;
    const next: string = [current.slice(0, position), event.key === 'Decimal' ? '.' : event.key, current.slice(position)].join('');
    if (next && !String(next).match(this.regex)) {
      event.preventDefault();
    }
  }
}

@Directive({
  selector: "input[numberFormatter]",   // usage: <input numberFormatter />
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberFormatterDirective),
      multi: true
    }
  ]
})
export class NumberFormatterDirective implements ControlValueAccessor {
  private onChange!: (value: any) => void;
  private onTouched!: () => void;

  constructor(private el: ElementRef<HTMLInputElement>) { }

  writeValue(value: any): void {
    if (value !== null && value !== undefined && value !== "") {
      this.el.nativeElement.value = this.format(value);
    } else {
      this.el.nativeElement.value = "";
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  /** Prevent invalid keys */
  @HostListener("keypress", ["$event"])
  onKeyPress(event: KeyboardEvent): void {
    const char = event.key;

    // Allow: digits, backspace, delete, arrows, tab, enter
    if (/^\d$/.test(char)) return;

    // Allow one decimal point
    if (char === "." && !this.el.nativeElement.value.includes(".")) return;

    event.preventDefault(); // Block everything else
  }

  /** Handle input change */
  @HostListener("input", ["$event.target.value"])
  onInput(value: string): void {
    const raw = this.parse(value);
    if (!isNaN(raw)) {
      this.onChange(raw); // update model with valid number
    } else {
      this.onChange(null); // invalid -> null
    }
  }

  /** Format on blur */
  @HostListener("blur")
  onBlur(): void {
    const raw = this.parse(this.el.nativeElement.value);
    if (!isNaN(raw)) {
      this.el.nativeElement.value = this.format(raw);
    } else {
      this.el.nativeElement.value = "";
    }
    if (this.onTouched) this.onTouched();
  }

  private format(value: number | string): string {
    return Number(value).toLocaleString("en-IN"); // Indian format
  }

  private parse(value: string): number {
    return Number(value.replace(/,/g, ""));
  }
}